import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  MessageSquareText, CheckCircle2, Clock, AlertTriangle, Smartphone,
} from 'lucide-react';

const Wrap = styled.div`display: flex; flex-direction: column; gap: 14px;`;

const Hint = styled.div`
  display: flex; align-items: center; gap: 10px;
  background: rgba(2, 145, 239, 0.06); border: 1px solid rgba(2, 145, 239, 0.2);
  border-radius: 12px; padding: 12px 14px;
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Card = styled.div`
  background: #fff; border: 1px solid #E8ECF0; border-radius: 12px; padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04); display: flex; align-items: center; gap: 12px;
`;
const IconWrap = styled.div`
  width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $bg }) => $bg}; color: ${({ $color }) => $color};
`;
const Body = styled.div`flex: 1; min-width: 0;`;
const CName = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const Meta = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;
const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 999px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $bg }) => $bg}; color: ${({ $color }) => $color};
`;
const Nps = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  flex-shrink: 0;
`;
const Empty = styled.div`
  text-align: center; padding: 40px 16px; color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const fmtDate = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang || undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function CheckInsTab({ checkIns }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const list = Array.isArray(checkIns) ? checkIns : [];

  return (
    <Wrap>
      <Hint>
        <Smartphone size={16} />
        {t('myOnboarding.checkins.mobileHint')}
      </Hint>

      {list.length === 0 && <Empty>{t('myOnboarding.checkins.empty')}</Empty>}

      {list.map((ci) => {
        const answered = !!ci.answeredAt;
        const v = ci.isLowNpsAlert
          ? { Icon: AlertTriangle, bg: 'rgba(229,57,53,0.10)', color: '#E53935' }
          : answered
            ? { Icon: CheckCircle2, bg: 'rgba(2,211,191,0.12)', color: '#01A899' }
            : { Icon: Clock, bg: 'rgba(2,145,239,0.10)', color: '#0291EF' };
        const dateText = fmtDate(ci.answeredAt, lang);

        return (
          <Card key={ci.id}>
            <IconWrap $bg={v.bg} $color={v.color}><v.Icon size={18} /></IconWrap>
            <Body>
              <CName>{ci.surveyName || t('myOnboarding.checkins.dayLabel', { day: ci.day })}</CName>
              <Meta>
                <Badge $bg="rgba(124,58,237,0.10)" $color="#7C3AED">
                  {t('myOnboarding.checkins.dayLabel', { day: ci.day })}
                </Badge>
                <Badge $bg={v.bg} $color={v.color}>
                  {answered ? t('myOnboarding.checkins.answered') : t('myOnboarding.checkins.pending')}
                </Badge>
                {ci.isLowNpsAlert && (
                  <Badge $bg="rgba(229,57,53,0.10)" $color="#E53935">
                    <MessageSquareText size={12} />{t('myOnboarding.checkins.lowNps')}
                  </Badge>
                )}
                {dateText && <span>{dateText}</span>}
              </Meta>
            </Body>
            {ci.npsScore != null && <Nps>{ci.npsScore}</Nps>}
          </Card>
        );
      })}
    </Wrap>
  );
}
